// Shared debounced-autosave primitives for the single-page CNE editor.
//
// AutosaveTextarea / AutosaveInput accept a value + onSave callback,
// debounce keystrokes for 1.2s, and surface a tiny saving / saved /
// error indicator inline. The underlying field exposes an imperative
// `flush()` via the `forwardRef`d ref — the parent calls flush() before
// "Save draft" or "Submit & lock" to commit any pending debounced save.

import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'

const DEBOUNCE_MS = 1200

// --------------------------------------------------------------------------- //
// Indicator
// --------------------------------------------------------------------------- //

function SavingIndicator({ state }) {
  if (state === 'saving') return <span className="text-[11px] text-slate-400">Saving…</span>
  if (state === 'saved')  return <span className="text-[11px] text-emerald-600">Saved</span>
  if (state === 'error')  return <span className="text-[11px] text-rose-600">Save failed</span>
  return <span className="text-[11px] text-slate-300">Auto-saves</span>
}

// --------------------------------------------------------------------------- //
// Section wrapper — one labelled block in the long scroll.
// --------------------------------------------------------------------------- //

export function Section({ title, hint, children, id }) {
  return (
    <section id={id} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
      <div className="flex items-baseline justify-between gap-3 mb-2">
        <h3 className="font-bold text-slate-800 text-base">{title}</h3>
        {hint && <span className="text-[11px] text-slate-400">{hint}</span>}
      </div>
      {children}
    </section>
  )
}

// --------------------------------------------------------------------------- //
// AutosaveTextarea — multi-line debounced field
// --------------------------------------------------------------------------- //

export const AutosaveTextarea = forwardRef(function AutosaveTextarea(
  { value = '', onSave, disabled, rows = 3, placeholder = '', label, sublabel, saveState = 'idle' },
  ref,
) {
  const [text, setText] = useState(value || '')
  const [lastSaved, setLastSaved] = useState(value || '')
  const timerRef = useRef(null)

  // Re-sync upstream changes when we have no unsaved local edits. The
  // important condition is `text === lastSaved` — that means the local
  // view is "in sync" with what we last committed, so any new upstream
  // value (e.g. a snippet inserted via mutateField, a refetch landing
  // in cache, an addendum from another tab) is a legitimate change
  // we should pick up. If the doctor has unsaved keystrokes
  // (text !== lastSaved), we skip so we don't overwrite their work.
  useEffect(() => {
    const v = value || ''
    if (text !== lastSaved) return  // unsaved local edits — don't clobber
    if (v === text) return          // already in sync
    setText(v)
    setLastSaved(v)
  }, [value]) // eslint-disable-line react-hooks/exhaustive-deps

  const flush = () => {
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = null
    if (text === lastSaved) return
    onSave?.(text)
    setLastSaved(text)
  }
  useImperativeHandle(ref, () => ({ flush }), [text, lastSaved])

  const handleChange = (next) => {
    setText(next)
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      if (next !== lastSaved) {
        onSave?.(next)
        setLastSaved(next)
      }
      timerRef.current = null
    }, DEBOUNCE_MS)
  }

  return (
    <div>
      {label && (
        <div className="flex items-baseline justify-between mb-1">
          <label className="text-xs font-semibold uppercase tracking-wide text-slate-600">
            {label}
            {sublabel && <span className="ml-1.5 text-[10px] font-normal text-slate-400 normal-case tracking-normal">{sublabel}</span>}
          </label>
          <SavingIndicator state={saveState} />
        </div>
      )}
      <textarea
        rows={rows}
        value={text}
        disabled={disabled}
        onChange={(e) => handleChange(e.target.value)}
        onBlur={flush}
        placeholder={placeholder}
        className="w-full border border-slate-300 focus:border-wantok-600 focus:outline-none rounded-lg px-3 py-2 text-sm leading-relaxed disabled:bg-slate-50"
      />
    </div>
  )
})

// --------------------------------------------------------------------------- //
// AutosaveInput — single-line short field (e.g. AMPLE last meal)
// --------------------------------------------------------------------------- //

export const AutosaveInput = forwardRef(function AutosaveInput(
  { value = '', onSave, disabled, placeholder = '', label, sublabel, saveState = 'idle' },
  ref,
) {
  const [text, setText] = useState(value || '')
  const [lastSaved, setLastSaved] = useState(value || '')
  const timerRef = useRef(null)

  // Same sync logic as AutosaveTextarea — accept upstream changes
  // when we have no unsaved local edits.
  useEffect(() => {
    const v = value || ''
    if (text !== lastSaved) return
    if (v === text) return
    setText(v)
    setLastSaved(v)
  }, [value]) // eslint-disable-line react-hooks/exhaustive-deps

  const flush = () => {
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = null
    if (text === lastSaved) return
    onSave?.(text)
    setLastSaved(text)
  }
  useImperativeHandle(ref, () => ({ flush }), [text, lastSaved])

  const handleChange = (next) => {
    setText(next)
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      if (next !== lastSaved) {
        onSave?.(next)
        setLastSaved(next)
      }
      timerRef.current = null
    }, DEBOUNCE_MS)
  }

  return (
    <div>
      {label && (
        <div className="flex items-baseline justify-between mb-1">
          <label className="text-xs font-semibold uppercase tracking-wide text-slate-600">
            {label}
            {sublabel && <span className="ml-1.5 text-[10px] font-normal text-slate-400 normal-case tracking-normal">{sublabel}</span>}
          </label>
          <SavingIndicator state={saveState} />
        </div>
      )}
      <input
        type="text"
        value={text}
        disabled={disabled}
        onChange={(e) => handleChange(e.target.value)}
        onBlur={flush}
        placeholder={placeholder}
        className="w-full border border-slate-300 focus:border-wantok-600 focus:outline-none rounded-lg px-3 py-2 text-sm disabled:bg-slate-50"
      />
    </div>
  )
})
